import { LogManager } from '@src/utility/Logger';

import { err, ok } from './common/Result';

import type { Result } from './common/Result';
import type { DistributionChannelRepository } from '@src/repositories/DistributionChannelRepository';
import type { DistributionChannel } from '@src/types.ts/distribution-channel';

const logger = LogManager.getLogger('service:DistributionChannel');

type RepositoryDeps = Pick<
  DistributionChannelRepository,
  | 'findEnabledForShowcase'
  | 'findAvailableForShowcase'
  | 'enableChannelForShowcase'
  | 'disableChannelForShowcase'
>;

export class DistributionChannelService {
  // Resolver indirection: repositories are constructed lazily by DatoClientStore
  // after auth is wired, so the service holds a getter rather than a direct ref.
  constructor(private readonly getRepository: () => RepositoryDeps) {}

  findEnabledForShowcase(
    showcaseId: string,
  ): Promise<Result<DistributionChannel[]>> {
    return this.run('load enabled distribution channels', { showcaseId }, repo =>
      repo.findEnabledForShowcase(showcaseId),
    );
  }

  findAvailableForShowcase(
    showcaseId: string,
  ): Promise<Result<DistributionChannel[]>> {
    return this.run(
      'load available distribution channels',
      { showcaseId },
      repo => repo.findAvailableForShowcase(showcaseId),
    );
  }

  enableChannelForShowcase(
    showcaseId: string,
    channelId: string,
  ): Promise<Result<void>> {
    return this.run(
      'enable distribution channel',
      { showcaseId, channelId },
      repo => repo.enableChannelForShowcase(showcaseId, channelId),
    );
  }

  disableChannelForShowcase(
    showcaseId: string,
    channelId: string,
  ): Promise<Result<void>> {
    return this.run(
      'disable distribution channel',
      { showcaseId, channelId },
      repo => repo.disableChannelForShowcase(showcaseId, channelId),
    );
  }

  private async run<T>(
    description: string,
    args: { showcaseId: string; channelId?: string },
    fn: (repo: RepositoryDeps) => Promise<T>,
  ): Promise<Result<T>> {
    if (!args.showcaseId) return err('VALIDATION', 'showcaseId is required');
    if ('channelId' in args && !args.channelId) {
      return err('VALIDATION', 'channelId is required');
    }
    let repository: RepositoryDeps;
    try {
      repository = this.getRepository();
    } catch (e) {
      logger.error('Distribution channel repository not initialized', e);
      return err(
        'PRECONDITION',
        e instanceof Error ? e.message : 'Repository not initialized',
        e,
      );
    }
    try {
      return ok(await fn(repository));
    } catch (e) {
      logger.error(`Failed to ${description}`, e);
      return err('EXTERNAL', e instanceof Error ? e.message : 'Unknown error', e);
    }
  }
}
